import { useState } from "react";
import {
  Badge,
  Chip,
  Grid2,
  Tooltip,
  Typography,
  IconButton,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Switch,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import WarningIcon from "@mui/icons-material/Warning";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import {
  stringAvatar,
  handleCopy,
  minifyAddress,
  buildDateString,
  colorPercent,
} from "../../support/utils";
import humanize from "humanize";
import LanguageIcon from "@mui/icons-material/Language";
import { Telegram, Twitter } from "@mui/icons-material";
import EmailIcon from "@mui/icons-material/Email";
import DescriptionIcon from "@mui/icons-material/Description";
import VerifiedIcon from "@mui/icons-material/Verified";
import Avatar from "../Avatar";
import { useAtom } from "jotai";
import { currentUserAtom } from "../../support/atoms";
import { useMutation, useQuery } from "urql";
import {
  updateTokenMutation,
  addSystemListValueMutation,
  removeSystemListValueMutation,
  systemListQuery,
} from "../../support/mutations";
import { useNotification } from "../../hooks/useNotification";
import { useTheme } from "@mui/material/styles";

const TokenAccordionSummary = ({ token, filters, approval, tag }) => {
  const [currentUser] = useAtom(currentUserAtom);
  const { showNotification } = useNotification();
  const [, updateToken] = useMutation(updateTokenMutation);
  const [, addSystemListValue] = useMutation(addSystemListValueMutation);
  const [, removeSystemListValue] = useMutation(removeSystemListValueMutation);
  const theme = useTheme();

  const [{ data: blacklistData }, reexecuteBlacklist] = useQuery({
    query: systemListQuery,
    variables: { type: "TOKEN_CHECKSUM_BLACKLIST" },
    pause: currentUser?.role !== "ADMIN" && currentUser?.role !== "MODERATOR",
  });
  const blacklist = blacklistData?.systemList || [];

  const [{ data: muteData }, reexecuteMute] = useQuery({
    query: systemListQuery,
    variables: { type: "NEW_WALLET_BUYS_MUTE" },
    pause: currentUser?.role !== "ADMIN" && currentUser?.role !== "MODERATOR",
  });
  const muteList = muteData?.systemList || [];
  const isMuted = muteList.includes(token.address);

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const menuOpen = Boolean(menuAnchorEl);
  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => setMenuAnchorEl(null);

  const handleToggleNewWalletBuysMute = async () => {
    const mutation = isMuted ? removeSystemListValue : addSystemListValue;
    try {
      const result = await mutation({
        type: "NEW_WALLET_BUYS_MUTE",
        value: token.address,
      });
      if (result.error) {
        showNotification(result.error.message, {
          severity: "error",
          autoHideDuration: 3000,
        });
      } else {
        showNotification(
          `New wallet buys ${isMuted ? "unmuted" : "muted"} for token`,
          {
            severity: "success",
            autoHideDuration: 3000,
          }
        );
        reexecuteMute({ requestPolicy: "network-only" });
      }
    } catch (error) {
      showNotification("Failed to update mute list", {
        severity: "error",
        autoHideDuration: 3000,
      });
    }
  };

  let symbol = token.symbol,
    name = token.name;
  if (!name) {
    name = "???";
  }
  if (!symbol) {
    symbol = "???";
  }
  //asads

  if (token.name.length > 30) name = token.name.substr(0, 30) + "...";
  if (token.symbol.length > 30) symbol = token.symbol.substr(0, 30) + "...";
  //TODO handle balances
  const deployerBalance = token.deployerBalance
    ? `${token.deployerBalance.toString().substr(0, 4)} ${token.network?.symbol || "ETHs"}`
    : "";
  const tokenString = `${name} (${symbol})`;

  const handleToggleBlacklist = async (value) => {
    const isBlacklisted = blacklist.includes(value);
    const mutation = isBlacklisted ? removeSystemListValue : addSystemListValue;
    try {
      const result = await mutation({
        type: "TOKEN_CHECKSUM_BLACKLIST",
        value: value,
      });
      if (result.error) {
        showNotification(result.error.message, {
          severity: "error",
          autoHideDuration: 3000,
        });
      } else {
        showNotification(
          `Checksum ${isBlacklisted ? "removed from" : "added to"} blacklist`,
          {
            severity: "success",
            autoHideDuration: 3000,
          }
        );
        reexecuteBlacklist({ requestPolicy: "network-only" });
      }
    } catch (error) {
      showNotification("Failed to update blacklist", {
        severity: "error",
        autoHideDuration: 3000,
      });
    }
  };

  const handleMarkAsScam = async () => {
    try {
      const result = await updateToken({
        input: {
          address: token.address,
          networkId: token.network.id,
          scam: !token.scamAt,
        },
      });
      if (result.error) {
        showNotification(result.error.message, {
          severity: "error",
          autoHideDuration: 3000,
        });
      } else {
        showNotification(
          `Token ${token.scamAt ? "unmarked" : "marked"} as scam`,
          {
            severity: "success",
            autoHideDuration: 3000,
          }
        );
      }
    } catch (error) {
      showNotification("Failed to update token scam status", {
        severity: "error",
        autoHideDuration: 3000,
      });
    }
  };

  return (
    <div style={{ float: "right", width: "100%" }}>
      <Typography
        fontSize={11}
        sx={{
          height: 10,
          display: "flex",
          float: "right",
          translate: { xs: [`30px -8px`], sm: [`70px -8px`] },
          whiteSpace: "nowrap",
        }}
      >
        {buildDateString(
          approval || token,
          filters.contractState,
          filters.marketState
        )}
      </Typography>

      <Grid2
        container
        spacing={1}
        sx={{
          width: "100%",
          pointerEvents: "auto",
        }}
      >
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          sx={{
            height: 60,
            paddingLeft: { xs: 1, sm: 0 },
            paddingBottom: { xs: 1, sm: 0 },
            marginBottom: { xs: 1, sm: 0 },
          }}
          badgeContent={
            token.socialsAt ? (
              <Tooltip title={`socials by ${token.socialsBy}`}>
                <VerifiedIcon
                  color="primary"
                  sx={{
                    height: 15,
                    width: 15,
                    backgroundColor: "white",
                    borderRadius: "100%", // Add the border radius here
                  }}
                />
              </Tooltip>
            ) : (
              ""
            )
          }
          color={theme.palette.text.primary}
        >
          <Avatar token={token} />
        </Badge>
        <Grid2
          size={{ xs: 9, sm: 9, md: 9, lg: 10, xl: 10 }}
          container
          sx={{ width: "100%", marginLeft: 2 }}
        >
          {tag}
          {approval && (
            <Grid2 size={12} justifyItems={"center"}>
              <Chip
                size="small"
                sx={{ maxWidth: { xs: "65vw", sm: "45vw", l: "50vw" } }}
                label={`${humanize.intword(approval.percent, ["", "k", "M", "B", "T", "Qd", "Qn", "Sx", "Sp", "Oc", "No", "De"])}% ${approval.nameTag}`}
                variant="outlined"
                color={colorPercent(approval.percent)}
                onClick={() => {
                  window.open(
                    `${approval.network.explorer}/tx/${approval.txHash}`,
                    "_blank"
                  );
                }}
                onDelete={() =>
                  window.open(
                    `${approval.network.explorer}/tx/${approval.txHash}`,
                    "_blank"
                  )
                }
                deleteIcon={<OpenInNewIcon />}
              />
            </Grid2>
          )}
          <Grid2 container sx={{ width: "100%" }} alignItems="center">
            <Grid2
              size={"auto"}
              sx={{
                display: "flex",
                maxWidth: { xs: "65vw", sm: "45vw", l: "50vw" },
              }}
              alignItems="center"
            >
              <Typography
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                onClick={() => handleCopy(token.name + " " + token.symbol)}
                variant="subtitle1"
              >
                {tokenString}
              </Typography>
              <Tooltip
                title={
                  currentUser?.role === "ADMIN" ||
                  currentUser?.role === "MODERATOR"
                    ? token.scamAt
                      ? "Unmark as scam"
                      : "Mark as scam"
                    : token.scamAt
                      ? "Marked as scam"
                      : "Not internally validated"
                }
              >
                <IconButton
                  size="small"
                  color={token.scamAt ? "error" : "default"}
                  onClick={
                    currentUser?.role === "ADMIN" ||
                    currentUser?.role === "MODERATOR"
                      ? handleMarkAsScam
                      : undefined
                  }
                  sx={{
                    marginLeft: 1,
                    padding: 0.25,
                    "& .MuiSvgIcon-root": {
                      fontSize: "1rem",
                    },
                    color: token.scamAt ? "error.main" : "text.secondary",
                    cursor:
                      currentUser?.role === "ADMIN" ||
                      currentUser?.role === "MODERATOR"
                        ? "pointer"
                        : "default",
                  }}
                >
                  <WarningIcon
                    sx={{
                      fill: token.scamAt ? "currentColor" : "none",
                      stroke: "currentColor",
                      strokeWidth: 1,
                    }}
                  />
                </IconButton>
              </Tooltip>
              {(currentUser?.role === "ADMIN" ||
                currentUser?.role === "MODERATOR") && (
                <>
                  <Tooltip title="Token actions">
                    <IconButton
                      size="small"
                      onClick={handleMenuOpen}
                      sx={{
                        marginLeft: 0.5,
                        padding: 0.25,
                        pointerEvents: "auto",
                        "& .MuiSvgIcon-root": { fontSize: "1rem" },
                      }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={menuAnchorEl}
                    open={menuOpen}
                    onClose={handleMenuClose}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MenuItem onClick={handleToggleNewWalletBuysMute}>
                      <ListItemIcon>
                        <NotificationsOffIcon
                          fontSize="small"
                          color={isMuted ? "error" : "inherit"}
                        />
                      </ListItemIcon>
                      <ListItemText>Mute new wallet buys</ListItemText>
                      <Switch
                        edge="end"
                        size="small"
                        checked={isMuted}
                        tabIndex={-1}
                      />
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Grid2>
            {(token.telegram ||
              token.twitter ||
              token.website ||
              token.whitepaper) && (
              <Grid2 size={"auto"} sx={{ display: "flex" }} alignItems="center">
                {token.telegram && (
                  <Telegram
                    onClick={() => window.open(token.telegram, "_blank")}
                  />
                )}
                {token.twitter && (
                  <Twitter
                    onClick={() => window.open(token.twitter, "_blank")}
                  />
                )}
                {token.website && (
                  <LanguageIcon
                    onClick={() => window.open(token.website, "_blank")}
                  />
                )}
                {token.website && (
                  <EmailIcon
                    onClick={() =>
                      window.open("mailto:" + token.website, "_blank")
                    }
                  />
                )}
                {token.whitepaper && (
                  <DescriptionIcon
                    onClick={() => window.open(token.whitepaper, "_blank")}
                  />
                )}
              </Grid2>
            )}

            <Grid2 size={"auto"} justifyItems={"center"}>
              <Chip
                size="small"
                label={`supply: ${humanize.intword(token.supply, ["", "k", "M", "B", "T", "Qd", "Qn", "Sx", "Sp", "Oc", "No", "De"])} - dec: ${token.decimals}`}
                variant="outlined"
              />
            </Grid2>
            {(token.buyTax !== null || token.sellTax !== null) && (
              <Grid2 size={"auto"} justifyItems={"center"}>
                <Chip
                  size="small"
                  label={
                    <Box sx={{ display: "inline-flex", gap: 0.5 }}>
                      <Typography
                        component="span"
                        sx={{
                          color: "success.main",
                          fontSize: "0.75rem",
                        }}
                      >
                        {token.buyTax ?? "?"}
                      </Typography>
                      <Typography component="span" sx={{ fontSize: "0.75rem" }}>
                        /
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          color: "error.main",
                          fontSize: "0.75rem",
                        }}
                      >
                        {token.sellTax ?? "?"}
                      </Typography>
                    </Box>
                  }
                  variant="outlined"
                  color={
                    token.buyTax > 10 || token.sellTax > 10
                      ? "warning"
                      : "default"
                  }
                />
              </Grid2>
            )}
          </Grid2>
          <Grid2 size={12}>
            <Tooltip title={`Copy address: ${token.address}`}>
              <Chip
                color={token.verifiedAt ? "success" : "default"}
                sx={{
                  marginRight: 1,
                  marginBottom: 1,
                  maxWidth: { xs: "65vw", sm: "45vw", l: "50vw" },
                }}
                size="small"
                label={"ca: " + (token.nameTag || minifyAddress(token.address))}
                variant="outlined"
                onClick={() => handleCopy(token.address)}
                onDelete={() =>
                  window.open(
                    `${token.network.explorer}/address/${token.address}`,
                    "_blank"
                  )
                }
                deleteIcon={<OpenInNewIcon />}
              />
            </Tooltip>
            {token.deployer && (
              <Tooltip title={`Copy dev address: ${token.deployer}`}>
                <Chip
                  sx={{
                    marginRight: 1,
                    marginBottom: 1,
                    maxWidth: { xs: "65vw", sm: "45vw", l: "50vw" },
                  }}
                  size="small"
                  label={
                    "dv: " +
                    deployerBalance +
                    " " +
                    (token.deployerNameTag || minifyAddress(token.deployer))
                  }
                  variant="filled"
                  onClick={() => handleCopy(token.deployer)}
                  onDelete={() =>
                    window.open(
                      `${token.network.explorer}/address/${token.deployer}`,
                      "_blank"
                    )
                  }
                  deleteIcon={<OpenInNewIcon />}
                />
              </Tooltip>
            )}
            {token.interfaceChecksum && (
              <Tooltip title={`Copy interface checksum`}>
                <Chip
                  sx={{ marginRight: 1, marginBottom: 1 }}
                  size="small"
                  label={`int: ${token.interfaceChecksum}`}
                  variant="filled"
                  onClick={() => handleCopy(token.interfaceChecksum)}
                  onDelete={
                    currentUser?.role === "ADMIN" ||
                    currentUser?.role === "MODERATOR"
                      ? () => handleToggleBlacklist(token.interfaceChecksum)
                      : () => handleCopy(token.interfaceChecksum)
                  }
                  deleteIcon={
                    currentUser?.role === "ADMIN" ||
                    currentUser?.role === "MODERATOR" ? (
                      <WarningIcon
                        sx={{
                          fill: blacklist.includes(token.interfaceChecksum)
                            ? `${theme.palette.error.main} !important`
                            : "none",
                          stroke: blacklist.includes(token.interfaceChecksum)
                            ? `${theme.palette.error.main} !important`
                            : "currentColor",
                          strokeWidth: 1,
                          fontSize: "1rem !important",
                          color: blacklist.includes(token.interfaceChecksum)
                            ? `${theme.palette.error.main} !important`
                            : "inherit",
                        }}
                      />
                    ) : (
                      <ContentCopyIcon />
                    )
                  }
                />
              </Tooltip>
            )}
            {token.deploymentChecksum && (
              <Tooltip title={`Copy deployment checksum`}>
                <Chip
                  sx={{ marginRight: 1, marginBottom: 1 }}
                  size="small"
                  label={`dep: ${token.deploymentChecksum}`}
                  variant="filled"
                  onClick={() => handleCopy(token.deploymentChecksum)}
                  onDelete={
                    currentUser?.role === "ADMIN" ||
                    currentUser?.role === "MODERATOR"
                      ? () => handleToggleBlacklist(token.deploymentChecksum)
                      : () => handleCopy(token.deploymentChecksum)
                  }
                  deleteIcon={
                    currentUser?.role === "ADMIN" ||
                    currentUser?.role === "MODERATOR" ? (
                      <WarningIcon
                        sx={{
                          fill: blacklist.includes(token.deploymentChecksum)
                            ? `${theme.palette.error.main} !important`
                            : "none",
                          stroke: blacklist.includes(token.deploymentChecksum)
                            ? `${theme.palette.error.main} !important`
                            : "currentColor",
                          strokeWidth: 1,
                          fontSize: "1rem !important",
                          color: blacklist.includes(token.deploymentChecksum)
                            ? `${theme.palette.error.main} !important`
                            : "inherit",
                        }}
                      />
                    ) : (
                      <ContentCopyIcon />
                    )
                  }
                />
              </Tooltip>
            )}
            {token.contractState === "PENDING" && (
              <Chip
                sx={{ marginRight: 1, marginBottom: 1 }}
                size="small"
                label={`pending...`}
                color="warning"
                variant="outlined"
                onClick={() => handleCopy(token.creationTx)}
                onDelete={() =>
                  window.open(
                    `${token.network.explorer}/tx/${token.creationTx}`,
                    "_blank"
                  )
                }
                deleteIcon={<OpenInNewIcon />}
              />
            )}
            {token.contractState === "DROPPED" && (
              <Chip
                sx={{ marginRight: 1, marginBottom: 1 }}
                size="small"
                label={`dropped`}
                color="error"
                variant="outlined"
                onClick={() => handleCopy(token.creationTx)}
                onDelete={() =>
                  window.open(
                    `${token.network.explorer}/tx/${token.creationTx}`,
                    "_blank"
                  )
                }
                deleteIcon={<OpenInNewIcon />}
              />
            )}
          </Grid2>
        </Grid2>
      </Grid2>
    </div>
  );
};

export default TokenAccordionSummary;
